import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  DialogActions,
  Button,
} from "@mui/material";
import { useGetChats } from "hooks/useGetChats";
import { useContext, useRef, useState } from "react";
import {
  FaCircle,
  FaPencilAlt,
  FaUser,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toChatTime } from "helpers/dateHelper";
import { SocketContext } from "context/SocketProvider";
import _ from "lodash";
import { toast } from "react-toastify";


interface TestChatsProps {
  selectedChatID: number | null;
  setSelectedChatID: (chatID: number | null) => void;
  isConnected: boolean;
  channelConnection: any | null;
}

/**
 * Список диалогов
 * @returns
 */
const TestChats = ({
  selectedChatID,
  setSelectedChatID,
  channelConnection,
}: TestChatsProps) => {
  const { send, isConnected } = useContext<any>(SocketContext);

  // Search query
  const [searchQuery, setSearchQuery] = useState<string>("");

  const { data: chats } = useGetChats({
    orgID: localStorage.getItem("orgID") as string,
    source: channelConnection?.source,
    externalAccountUserName: channelConnection?.externalAccountUserName,
  });

  const navigate = useNavigate();

  // Dialogs
  const [isCreateChatDialogOpen, setIsCreateChatDialogOpen] = useState<boolean>(false);
  const externalAccountID = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLInputElement>(null);


  // Это для создания чата вручную используются для тестового канала TEST_SANDBOX
  const createChat = async (e: any) => {
    e.preventDefault();
    try {
      if (!externalAccountID.current || !externalAccountID.current.value) {
        toast.error("Идентификатор пользователя в внешней системе не может быть пустым!");
        return;
      }

      let externalUserID = externalAccountID.current.value || "";
      let firstMessage = textRef.current?.value || "";

      if (!firstMessage) {
        toast.error("Первое сообщение не может быть пустым!");
        return;
      }
      if (!externalUserID) {
        toast.error("Идентификатор пользователя в внешней системе не может быть пустым!");
        return;
      }

      send({
        event: "chat:initialize",
        data: {
          text: firstMessage,
          chatID: externalUserID,
          channelConnectionID: channelConnection?.id,
        },
      });

      toast.success("Чат создан успешно!");
      setIsCreateChatDialogOpen(false);
    } catch (error) {
      toast.error("Ошибка при создании чата!");
    }
  };

  return (
    <div className="chat-list">
      <div className="chat-list-header flex flex-col">
        <div id="chats-header-title" className="mb-4">
          <span className="text-md font-bold">Все чаты</span>
        </div>
        <div id="chats-search" className="w-full">
          <TextField
            label="Поиск"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              endAdornment: (
                <div className="flex items-center justify-center">
                  <FaCircle color={isConnected ? "green" : "orange"} size={12} />
                </div>
              ),
            }}
          />
        </div>
      </div>
      <div className="chat-list-body">
        {chats
          ?.map((chat: any) => (
            <div
              className={`chat-list-item ${selectedChatID === chat.id ? "selected" : ""}`}
              key={chat.id}
              onClick={() => {
                // add chatID to query params
                navigate(`?chatID=${chat.id}`, { replace: true });
                setSelectedChatID(chat.id ?? null);
              }}
            >
              <div className="chat-list-item-name">
                <div className="flex items-center justify-between gap-2">
                  <div className="left">
                    <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
                      {chat.externalUserPhoto ? (
                        <img
                          src={chat.externalUserPhoto}
                          alt="User Photo"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FaUser size={20} />
                      )}
                    </div>
                  </div>
                  <div className="right flex-1 overflow-hidden">
                    <div className="flex items-center justify-between gap-2">
                      <div
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        {_.get(chat, "contact.externalUserFirstName") ||
                          _.get(chat, "contact.externalUserUserName") ||
                          _.get(chat, "contact.externalUserID") ||
                          _.get(chat, "externalUserFirstName")}
                      </div>
                      <div className="text-xs">{toChatTime(chat.updatedAt)}</div>
                    </div>
                    <div
                      className="text-sm text-gray"
                      style={{
                        fontWeight: 400,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 220,
                      }}
                    >
                      {chat.lastMessage ? chat.lastMessage : "Нет сообщений"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
      <div className="chat-list-footer">
        <IconButton color="primary" onClick={() => setIsCreateChatDialogOpen(true)}>
          <FaPencilAlt size={24} />
        </IconButton>
      </div>
      <Dialog
        open={isCreateChatDialogOpen}
        onClose={() => setIsCreateChatDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={createChat}>
          <DialogTitle>Новый чат</DialogTitle>
          <DialogContent>
            <div className="p-4 flex flex-col gap-2">
              <div className="text-sm text-gray-600 mb-2">
                Напишите агенту так, как написал бы клиент. Ответ придёт через несколько секунд.
              </div>
              <TextField
                label="Имя тестового клиента"
                helperText="Любое: например, «Айгерим». Для каждого имени создаётся отдельный чат"
                fullWidth
                defaultValue={`client-${Date.now().toString().slice(-4)}`}
                inputRef={externalAccountID}
              />
              <div>
                <TextField
                  label="Первое сообщение"
                  placeholder="Здравствуйте! Сколько стоит ваша услуга?"
                  fullWidth
                  inputRef={textRef}
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="primary" fullWidth type="submit">
              Создать чат
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default TestChats;
